import React, {
  forwardRef, useCallback, useEffect, useMemo, useState,
} from 'react';

import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { Input } from '@vlabs/uikit/components/controls/input';
import { Radio } from '@vlabs/uikit/components/controls/radio';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

import type { ScoreField } from './VideoAnalyticsScoreFilterPopover';
import { useWorkspaceFilters } from '../../useWorkspaceFilters';

interface FacecoverScoreField extends ScoreField {
  key: string;
}

interface FacecoverScoreFilterPopoverProps extends PopoverWithButtonProps {
  scoreFields: FacecoverScoreField[];
}

function getValidationError(value: string, scoreField: ScoreField): string | undefined {
  if (value === '') return undefined;
  const validationResult = scoreField.validate?.validate?.(value);
  if (validationResult !== undefined && validationResult !== true) return String(validationResult);
  const numberValue = Number(value);
  const min = scoreField.validate?.min;
  if (min !== undefined && numberValue < min.value) return String(min.message);
  const max = scoreField.validate?.max;
  if (max !== undefined && numberValue > max.value) return String(max.message);
  return undefined;
}

function FacecoverScoreFilterPopover(
  {
    scoreFields,
    ...props
  }: FacecoverScoreFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { t } = useTranslation();
  const { filters, updateVideoAnalyticsFilters } = useWorkspaceFilters();
  const { facecover } = filters.videoAnalytics;
  const selectedKey = String(facecover.filters.subtype ?? '');
  const activeField = scoreFields.find(({ key }) => key === selectedKey);
  const activeFilterName = activeField?.filterName;
  const storedValue = activeFilterName ? String(facecover.filters[activeFilterName] ?? '') : '';
  const [value, setValue] = useState(storedValue);
  const error = activeField ? getValidationError(value, activeField) : undefined;

  useEffect(() => setValue(storedValue), [storedValue]);
  const updateFacecoverFilters = useCallback((nextFilters: Record<string, unknown>) => {
    updateVideoAnalyticsFilters('facecover', nextFilters);
  }, [updateVideoAnalyticsFilters]);

  const updateScore = useMemo(
    () => debounce(updateFacecoverFilters, 500),
    [updateFacecoverFilters],
  );

  useEffect(() => () => updateScore.cancel(), [updateScore]);

  const selectField = (field: FacecoverScoreField) => {
    updateScore.cancel();
    const nextFilters = { ...facecover.filters, subtype: field.key };
    scoreFields.forEach((scoreField) => {
      if (scoreField.key !== field.key) {
        nextFilters[scoreField.filterName] = undefined;
      }
    });
    setValue(String(nextFilters[field.filterName] ?? ''));
    updateFacecoverFilters(nextFilters);
  };

  const changeValue = (field: FacecoverScoreField, nextValue: string) => {
    setValue(nextValue);
    if (getValidationError(nextValue, field) !== undefined) return;
    updateScore({
      ...facecover.filters,
      [field.filterName]: nextValue || undefined,
    });
  };

  const reset = () => {
    updateScore.cancel();
    const nextFilters = { ...facecover.filters, subtype: undefined };
    scoreFields.forEach((field) => {
      nextFilters[field.filterName] = undefined;
    });
    setValue('');
    updateFacecoverFilters(nextFilters);
  };

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={!selectedKey && !storedValue}
      onClickReset={reset}
      ref={ref}
    >
      {scoreFields.map((field) => {
        const isActive = selectedKey === field.key;
        return (
          <div key={field.key}>
            <Radio
              checked={isActive}
              id={`facecover-score.${field.key}`}
              label={t(field.labelKey)}
              onChange={() => selectField(field)}
            />
            <Input
              disabled={!isActive}
              errorMessage={isActive ? error : undefined}
              hasError={Boolean(isActive && error !== undefined)}
              id={field.id}
              isClearable={isActive}
              label={t('videoAnalytics:filters.score.valueLabel', { defaultValue: 'Порог, %' })}
              onChangeValue={(nextValue) => changeValue(field, nextValue)}
              onClear={() => changeValue(field, '')}
              placeholder={t('from')}
              value={isActive ? value : ''}
            />
          </div>
        );
      })}
    </PopoverWithButton>
  );
}

const FacecoverScoreFilterPopoverWithRef = forwardRef(FacecoverScoreFilterPopover);
export { FacecoverScoreFilterPopoverWithRef as FacecoverScoreFilterPopover };
